import { DEFAULT_REDACTION, redactSensitiveInfo } from '@repo/lib/strings';

const SENSITIVE_KEY =
  /^(?:token|password|authorization|cookie|secret|email|zipcode|user_ip|x-authentication|bearer)$/i;

const MAX_STRING_LEN = 4000;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/**
 * Recursively redact sensitive keys and patterns in objects sent to Rollbar
 * as `custom.request` / `custom.response` (replacing blanket scrubPaths).
 */
export const sanitizeRollbarStructuredData = (
  value: unknown,
  depth = 0,
): unknown => {
  if (depth > 10) {
    return '[Truncated depth]';
  }
  if (value === null || value === undefined) {
    return value;
  }
  if (typeof value === 'string') {
    return redactSensitiveInfo(value).slice(0, MAX_STRING_LEN);
  }
  if (typeof value !== 'object') {
    return value;
  }
  if (Array.isArray(value)) {
    return value.map((entry) =>
      sanitizeRollbarStructuredData(entry, depth + 1),
    );
  }
  const out: Record<string, unknown> = {};
  for (const [key, entry] of Object.entries(value)) {
    if (SENSITIVE_KEY.test(key)) {
      out[key] = DEFAULT_REDACTION;
      continue;
    }
    out[key] = sanitizeRollbarStructuredData(entry, depth + 1);
  }
  return out;
};

export const sanitizePayloadCustom = (payload: {
  custom?: unknown;
  body?: unknown;
  data?: unknown;
}): void => {
  if (payload.custom !== undefined) {
    payload.custom = sanitizeRollbarStructuredData(payload.custom);
  }
  if (isRecord(payload.body) && 'custom' in payload.body) {
    const custom = payload.body.custom;
    if (custom !== undefined) {
      payload.body = {
        ...payload.body,
        custom: sanitizeRollbarStructuredData(custom),
      };
    }
  }
  if (isRecord(payload.data) && 'custom' in payload.data) {
    const custom = payload.data.custom;
    if (custom !== undefined) {
      payload.data = {
        ...payload.data,
        custom: sanitizeRollbarStructuredData(custom),
      };
    }
  }
};
